import React, { useState, useEffect, useCallback } from 'react';
import { FaCalendarAlt, FaUserCheck } from 'react-icons/fa';
import { 
  faChartPie,
  faChartBar,
  faUsers,
  faListAlt,faCalendarAlt
} from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';

// Presentational helper only -- maps a record's existing `status` value to the
// shared pill style/label. Does not affect what status is stored or fetched.
function statusPill(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'present') return { cls: 'pill-success', label: 'Present' };
  if (s === 'late') return { cls: 'pill-warning', label: 'Late' };
  if (s === 'absent') return { cls: 'pill-danger', label: 'Absent' };
  return { cls: 'pill-info', label: status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown' };
}

const AttendancePage = () => {
  const [eventId, setEventId] = useState(null);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [memberNames, setMemberNames] = useState({});

  // Function to fetch member name by SLIIT ID
  const fetchMemberName = async (sliitId) => {
    if (!sliitId) return 'Unknown Member';
    
    // Return cached name if available
    if (memberNames[sliitId]) {
      return memberNames[sliitId];
    }
    
    try {
      const response = await fetch(`https://dev.sliitleo.org/Backend/get_member.php?sliitId=${sliitId}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      const result = await response.json();
      
      if (response.ok && result.success && result.data) {
        const name = result.data.fullname || result.data.FullName || 'Unknown Member';
        // Update cache
        setMemberNames(prev => ({
          ...prev,
          [sliitId]: name
        }));
        return name;
      }
      return 'Unknown Member';
    } catch (error) {
      console.error('Error fetching member name:', error);
      return 'Unknown Member';
    }
  };

  const fetchAttendanceRecords = useCallback(async () => {
    if (eventId === null || eventId === '') {
      console.log('No event ID provided, skipping fetch');
      return;
    }
    
    // console.log(`Fetching attendance for event ID: ${eventId}`);
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`https://dev.sliitleo.org/Backend/attendance_api.php?event_id=${eventId}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
        },
      });
      
      // console.log('Attendance API Response Status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`Server responded with status ${response.status}`);
      }
      
      const result = await response.json();
      // console.log('Attendance API Response:', result);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch attendance records');
      }
      
      if (!result.data || result.data.length === 0) {
        console.log('No attendance records found for this event');
        setAttendanceRecords([]);
        return;
      }
      
      // Format the attendance records
      const formattedRecords = await Promise.all(result.data.map(async (record) => {
        try {
          const memberName = await fetchMemberName(record.member_id);
          return {
            id: record.id,
            member_id: record.member_id,
            event_id: record.event_id,
            check_in_time: record.check_in_time || record.created_at,
            status: record.status || 'present',
            checked_by: record.checked_by || 'System',
            event_name: record.event_name,
            event_date: record.event_date, // Add event date
            location: record.location,     // Add location
            fullname: memberName,
            created_at: record.created_at,
            updated_at: record.updated_at || record.created_at
          };
        } catch (err) {
          console.error('Error formatting record:', err);
          return null;
        }
      }));
      
      // Filter out any null records from errors
      const validRecords = formattedRecords.filter(record => record !== null);
      // console.log('Formatted attendance records:', validRecords);
      
      setAttendanceRecords(validRecords);
    } catch (err) {
      console.error('Error in fetchAttendanceRecords:', err);
      setError(err.message || 'Failed to fetch attendance records');
    } finally {
      setIsLoading(false);
    }
  }, [eventId, memberNames]);

  const user = JSON.parse(localStorage.getItem('user')) || {
    name: 'Unknown User',
    SLIITID: '0',
    role: 'Member',
    image: '/user.png',
  };

  const fetchEvents = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await fetch('https://dev.sliitleo.org/Backend/attendance_api.php?action=get_events', {
        credentials: 'include',
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch events');
      }
      
      const data = await response.json();
      
      if (data.success && data.data) {
        setEvents(data.data);
        // If there are events, select the first one by default
        if (data.data.length > 0) {
          setEventId(data.data[0].id);
        }
      }
    } catch (err) {
      console.error('Error fetching events:', err);
      setError(err.message || 'Failed to load events');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load events on component mount
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setIsLoading(true);
        const response = await fetch('https://dev.sliitleo.org/Backend/attendance_api.php', {
          credentials: 'include',
          headers: {
            'Accept': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        // console.log('Events API Response:', data); // Debug log
        
        if (data.success && data.data) {
          setEvents(data.data);
          // If there are events, select the first one by default
          if (data.data.length > 0) {
            setEventId(data.data[0].id);
          }
        } else {
          throw new Error(data.error || 'No events data received');
        }
      } catch (err) {
        console.error('Error fetching events:', err);
        setError(err.message || 'Failed to load events');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchEvents();
  }, []);

  // Update attendance records when eventId changes
  useEffect(() => {
    if (eventId !== null) {
      fetchAttendanceRecords();
    }
  }, [eventId, fetchAttendanceRecords]);

  return (
    <>
      <Sidebar />
      <Topbar user={user} />

      <main className="lg:ml-64 mt-14 px-4 sm:px-6 md:px-8 py-6 md:py-10">
        {/* Header */}
        <div className="page-head">
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-4)' }}>
            <span
              style={{
                width: 44, height: 44, borderRadius: 'var(--radius-md)', flexShrink: 0,
                display: 'grid', placeItems: 'center', background: 'var(--accent)', color: '#fff',
              }}
            >
              <FaUserCheck size={18} />
            </span>
            <div>
              <h1>Attendance Management</h1>
              <p className="page-desc">View and manage attendance records</p>
            </div>
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2" style={{ marginBottom: 'var(--sp-6)' }}>
          <Link to="/members" className="btn btn-ghost btn-sm" style={{ justifyContent: 'center' }}>
            <FontAwesomeIcon icon={faUsers} /> <span className="truncate">Members</span>
          </Link>
          <Link to="/events" className="btn btn-ghost btn-sm" style={{ justifyContent: 'center' }}>
            <FontAwesomeIcon icon={faChartPie} /> <span className="truncate">Events</span>
          </Link>
          <Link to="/scan-qr-code" className="btn btn-ghost btn-sm" style={{ justifyContent: 'center' }}>
            <FontAwesomeIcon icon={faChartBar} /> <span className="truncate">Scan QR</span>
          </Link>
          <Link to="/attendance" className="btn btn-primary btn-sm" style={{ justifyContent: 'center' }}>
            <FontAwesomeIcon icon={faListAlt} /> <span className="truncate">Attendance</span>
          </Link>
        </div>

        <div className="panel" style={{ padding: 'var(--sp-5)' }}>
          {/* Event Selection and Search */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" style={{ marginBottom: 'var(--sp-5)' }}>
            <div className="field" style={{ marginBottom: 0 }}>
              <label htmlFor="event-select" className="field-label">
                Select Event
              </label>
              <select
                id="event-select"
                value={eventId || ''}
                onChange={(e) => {
                  const newEventId = e.target.value ? Number(e.target.value) : null;
                  setEventId(newEventId);
                }}
              >
                {events.map((event) => (
                  <option key={event.id} value={event.id}>
                    {event.event_name} - {new Date(event.event_date).toLocaleDateString()}
                  </option>
                ))}
              </select>
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label htmlFor="search" className="field-label">
                Search Members
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  id="search"
                  placeholder="Search by name or ID..."
                  style={{ paddingRight: 34 }}
                />
                <span
                  aria-hidden="true"
                  style={{
                    position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                    color: 'var(--text-3)', pointerEvents: 'none', display: 'grid',
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </span>
              </div>
            </div>
          </div>

          {/* Attendance Records Table */}
          {isLoading ? (
            <div className="empty-state">
              <div className="spinner lg" />
              <p>Loading attendance records…</p>
            </div>
          ) : error ? (
            <div className="error-banner">{error}</div>
          ) : attendanceRecords.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon"><FaUserCheck size={18} /></div>
              <h3>No attendance records</h3>
              <p>No attendance records found for this event.</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>Member ID</th>
                    <th>Name</th>
                    <th>Time</th>
                    <th className="hidden md:table-cell">Checked by</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {attendanceRecords.map((record) => {
                    const { cls, label } = statusPill(record.status);
                    return (
                      <tr key={record.id}>
                        <td className="num">{record.member_id}</td>
                        <td>{record.fullname || 'Loading…'}</td>
                        <td>
                          {record.created_at ? (
                            new Date(record.created_at).toLocaleString('en-LK', {
                              timeZone: 'Asia/Colombo',
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: true
                            })
                          ) : 'Loading…'}
                        </td>
                        <td className="hidden md:table-cell">
                          {record.checked_by || 'System'}
                        </td>
                        <td>
                          <span className={`pill ${cls}`}><span className="dot" />{label}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Event Info Card */}
        {!isLoading && attendanceRecords.length > 0 && (
          <div className="panel" style={{ marginTop: 'var(--sp-6)', padding: 'var(--sp-5)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--sp-3)', flexWrap: 'wrap', marginBottom: 'var(--sp-4)' }}>
              <h2 className="truncate" style={{ fontSize: 16, fontWeight: 700, maxWidth: '70%' }} title={attendanceRecords[0]?.event_name || 'Event Details'}>
                <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: 8, color: 'var(--link)' }} />
                {attendanceRecords[0]?.event_name || 'Event Details'}
              </h2>
              <span className="pill pill-info">
                <span className="dot" />
                {attendanceRecords.length} {attendanceRecords.length === 1 ? 'attendee' : 'attendees'}
              </span>
            </div>

            <div className="grid grid-cols-1 xs:grid-cols-2 gap-4">
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div
                  style={{
                    flexShrink: 0, width: 36, height: 36, borderRadius: 'var(--radius-sm)',
                    background: 'var(--surface-2)', border: '1px solid var(--border)',
                    display: 'grid', placeItems: 'center', color: 'var(--link)',
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <p className="field-label" style={{ marginBottom: 2 }}>Event Date &amp; Time</p>
                  <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-1)' }}>
                    {attendanceRecords[0]?.event_date ? (
                      new Date(attendanceRecords[0].event_date).toLocaleString('en-LK', {
                        timeZone: 'Asia/Colombo',
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true
                      })
                    ) : 'Date not specified'}
                  </p>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, minWidth: 0 }}>
                <div
                  style={{
                    flexShrink: 0, width: 36, height: 36, borderRadius: 'var(--radius-sm)',
                    background: 'var(--surface-2)', border: '1px solid var(--border)',
                    display: 'grid', placeItems: 'center', color: 'var(--link)',
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div className="truncate" style={{ minWidth: 0 }}>
                  <p className="field-label" style={{ marginBottom: 2 }}>Location</p>
                  <p
                    className="truncate"
                    style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-1)' }}
                    title={attendanceRecords[0]?.location || 'Location not specified'}
                  >
                    {attendanceRecords[0]?.location || 'Location not specified'}
                  </p>
                </div>
              </div>
            </div>

            {/* Additional Event Information */}
            <div style={{ marginTop: 'var(--sp-4)', paddingTop: 'var(--sp-4)', borderTop: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div
                  style={{
                    flexShrink: 0, width: 36, height: 36, borderRadius: 'var(--radius-sm)',
                    background: 'var(--surface-2)', border: '1px solid var(--border)',
                    display: 'grid', placeItems: 'center', color: 'var(--link)',
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="field-label" style={{ marginBottom: 2 }}>Last Updated</p>
                  <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-1)' }}>
                    {attendanceRecords[0]?.updated_at ? (
                      new Date(attendanceRecords[0].updated_at).toLocaleString('en-LK', {
                        timeZone: 'Asia/Colombo',
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })
                    ) : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </>
  );
};

export default AttendancePage;
